import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { useAuth } from "@/core/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/ui/avatar";
import { Badge } from "@/shared/ui/badge";
import { Separator } from "@/shared/ui/separator";
import { User, Mail, Building2, Shield, Bell, BellOff } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import { usePushNotifications } from "@/core/hooks/usePushNotifications";
import { useFactory } from "@/core/hooks/useFactory";
import { useEffect } from "react";

export default function Profile() {
  const { user, profile } = useAuth();
  const { selectedFactory } = useFactory();
  const {
    isSupported,
    permission,
    subscription,
    isLoading,
    subscribe,
    unsubscribe,
    sendLocalNotification,
    saveToDatabase,
  } = usePushNotifications();

  useEffect(() => {
    // Register service worker on mount
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(console.error);
    }
  }, []);

  const initials = (profile?.full_name || user?.email || 'U')
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const handleTestNotification = () => {
    sendLocalNotification('Test Alert', {
      body: 'This is a test notification from Factory Pilot.',
      tag: 'test-notification',
    });
  };

  const handleToggleSubscription = async () => {
    if (subscription) {
      await unsubscribe();
    } else {
      const sub = await subscribe();
      // Save to database with current factory
      if (sub && selectedFactory) {
        await saveToDatabase(selectedFactory.id);
      }
    }
  };

  return (
    <div className="container max-w-4xl mx-auto py-6 px-4 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Profile Settings</h1>
        <p className="text-muted-foreground">Manage your account and notification preferences</p>
      </div>

      {/* Profile Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Account Information
          </CardTitle>
          <CardDescription>Your personal profile details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={profile?.avatar_url || undefined} />
              <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-lg font-semibold">{profile?.full_name || 'User'}</h3>
              <p className="text-muted-foreground">{profile?.job_title || 'No job title set'}</p>
            </div>
          </div>

          <Separator />

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Email</Label>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span>{user?.email}</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-muted-foreground">Department</Label>
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span>{profile?.department || 'Not specified'}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Employee ID</Label>
              <span>{profile?.employee_id || 'Not specified'}</span>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Role</Label>
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-muted-foreground" />
                {profile?.is_super_admin ? (
                  <Badge variant="default">Super Admin</Badge>
                ) : (
                  <span>Standard User</span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Push Notifications Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Push Notifications
          </CardTitle>
          <CardDescription>
            Configure browser push notifications for AI Production alerts and other notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!isSupported ? (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 text-destructive">
              <BellOff className="h-5 w-5" />
              <div>
                <p className="font-medium">Not Supported</p>
                <p className="text-sm">Push notifications are not supported in this browser.</p>
              </div>
            </div>
          ) : (
            <>
              {/* Status */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  {subscription ? (
                    <Bell className="h-5 w-5 text-success" />
                  ) : (
                    <BellOff className="h-5 w-5 text-muted-foreground" />
                  )}
                  <div>
                    <p className="font-medium">Notification Status</p>
                    <p className="text-sm text-muted-foreground">
                      {permission === 'granted' && subscription
                        ? 'Push notifications are enabled'
                        : permission === 'denied'
                        ? 'Notifications are blocked by your browser'
                        : 'Push notifications are not enabled'}
                    </p>
                  </div>
                </div>
                <div
                  className={`h-3 w-3 rounded-full ${
                    subscription ? "bg-success" : permission === "denied" ? "bg-destructive" : "bg-warning"
                  }`}
                />
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                {permission !== 'denied' && (
                  <Button
                    onClick={handleToggleSubscription}
                    disabled={isLoading}
                    className="flex-1"
                  >
                    {isLoading
                      ? 'Loading...'
                      : subscription
                      ? 'Disable Push Notifications'
                      : 'Enable Push Notifications'}
                  </Button>
                )}

                <Button
                  variant="outline"
                  onClick={handleTestNotification}
                  className="flex-1"
                >
                  Send Test Notification
                </Button>
              </div>

              {permission === 'denied' && (
                <div className="p-4 rounded-lg bg-destructive/10 text-destructive">
                  <p className="text-sm">
                    Notifications are blocked by your browser. To enable them:
                  </p>
                  <ol className="text-sm mt-2 ml-4 list-decimal">
                    <li>Click the lock/info icon in your browser's address bar</li>
                    <li>Find "Notifications" in the site settings</li>
                    <li>Change the setting to "Allow"</li>
                    <li>Refresh this page</li>
                  </ol>
                </div>
              )}

              {/* Info */}
              <div className="text-sm text-muted-foreground space-y-2 pt-4 border-t">
                <p className="font-medium">About Push Notifications</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li>Works on desktop browsers (Chrome, Firefox, Edge, Safari)</li>
                  <li>Works on Android devices with supported browsers</li>
                  <li>iOS Safari requires adding this app to your home screen (PWA)</li>
                  <li>Notifications will be sent even when the browser tab is in the background</li>
                </ul>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
